import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { green01 } from '../../theme';

const DEG_TO_RAD = Math.PI / 180;

interface PolarCoordinate {
  x: number;
  y: number;
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number): PolarCoordinate {
  const rad = (angleDeg - 90) * DEG_TO_RAD;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

interface CircularProgressProps {
  fillPercent?: number;
  size?: number;
  stroke?: number;
  spinDuration?: number;
  completed?: boolean;
  label?: string;
}

export default function CircularProgress({
  fillPercent = 80,
  size = 120,
  stroke = 14,
  spinDuration = 2400,
  completed = false,
  label,
}: CircularProgressProps) {
  const { t } = useTranslation();
  // ── Animate the fill on mount (0 → fillPercent) ──────────────
  const [animatedFill, setAnimatedFill] = useState(0);

  useEffect(() => {
    let raf: number;
    const start = performance.now();
    const duration = 1000;
    const target = Math.min(100, Math.max(0, fillPercent));

    function tick(now: number) {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setAnimatedFill(target * ease);
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fillPercent]);

  // ── Geometry ─────────────────────────────────────────────────
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - stroke) / 2;

  const ARC_SPAN = 360;
  const pad = 10;
  const progressAngle = (ARC_SPAN * animatedFill) / 100;

  const dot = polarToCartesian(cx, cy, r, progressAngle);

  const gradStart = polarToCartesian(cx, cy, r, 0);
  const gradMid = polarToCartesian(cx, cy, r, ARC_SPAN * 0.45);

  const gradId = 'progress-grad';

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox={`${-pad} ${-pad} ${size + pad * 2} ${size + pad * 2}`} fill="none">
          {/* ── Inline keyframes for the spin ── */}
          <style>{`
            @keyframes spin-progress {
              from { transform: rotate(0deg); }
              to   { transform: rotate(360deg); }
            }
          `}</style>

          {/* Outer border */}
          <circle cx={cx} cy={cy} r={r + stroke / 2} stroke="#232323" strokeWidth={10} fill="none" />

          {/* Inner border */}
          <circle cx={cx} cy={cy} r={r - stroke / 2} stroke="#232323" strokeWidth={10} fill="none" />

          {/* Static background track */}
          <circle cx={cx} cy={cy} r={r} stroke="#232323" strokeWidth={stroke} fill="none" />

          <defs>
            <linearGradient
              id={gradId}
              x1={gradStart.x}
              y1={gradStart.y}
              x2={gradMid.x}
              y2={gradMid.y}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={green01} />
              <stop offset="100%" stopColor={green01} stopOpacity="0" />
            </linearGradient>

            <linearGradient
              id={`${gradId}-tip`}
              x1={gradMid.x}
              y1={gradMid.y}
              x2={dot.x}
              y2={dot.y}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={green01} stopOpacity="0" />
              <stop offset="100%" stopColor={green01} />
            </linearGradient>
          </defs>

          {completed ? (
            <circle cx={cx} cy={cy} r={r} stroke={green01} strokeWidth={stroke - 2} fill="none" />
          ) : (
            /*
              ── Spinning group ──
              Everything inside rotates around the centre.
              The gradient defs above use userSpaceOnUse, but because the
              whole group spins, the gradient rotates *with* the arc —
              which is exactly the effect we want.
            */
            <g
              style={{
                transformOrigin: `${cx}px ${cy}px`,
                animation: `spin-progress ${spinDuration}ms linear infinite`,
              }}
            >
              {/* Progress arc (two-part gradient) */}
              {animatedFill > 0.4 && (
                <>
                  <path
                    d={describeArc(cx, cy, r, 0, Math.min(progressAngle, ARC_SPAN * 0.45))}
                    stroke={`url(#${gradId})`}
                    strokeWidth={stroke - 2}
                    strokeLinecap="round"
                    fill="none"
                  />
                  {animatedFill > 45 && (
                    <path
                      d={describeArc(cx, cy, r, ARC_SPAN * 0.44, progressAngle)}
                      stroke={`url(#${gradId}-tip)`}
                      strokeWidth={stroke - 2}
                      strokeLinecap="round"
                      fill="none"
                    />
                  )}
                </>
              )}

              {/* White dot at the tip */}
              <circle
                cx={dot.x}
                cy={dot.y}
                r={stroke / 3}
                fill="#FFFFFF"
                style={{ filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.25))' }}
              />
            </g>
          )}
        </svg>

        {/* Centre label — static, doesn't spin */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            paddingLeft: stroke + 40,
            width: '250px',
          }}
        >
          <div
            style={{
              fontSize: size * 0.04,
              fontWeight: 500,
              color: green01,
              lineHeight: 1.3,
              fontFamily: 'system-ui, sans-serif',
              textAlign: 'left',
            }}
          >
            {completed ? (
              <div style={{ textAlign: 'center' }}>
                <h3
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: green01,
                    margin: 0,
                    fontFamily: 'system-ui, sans-serif',
                  }}
                >
                  {t('workspace.marketMatchCompleted')}
                </h3>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: green01,
                    opacity: 0.8,
                    fontFamily: 'system-ui, sans-serif',
                  }}
                >
                  {t('workspace.marketMatchCompletedDescription')}
                </span>
              </div>
            ) : (
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: green01,
                  lineHeight: 1.3,
                  fontFamily: 'system-ui, sans-serif',
                  textAlign: 'center',
                }}
              >
                {label || t('workspace.marketMatchSearching')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
